/**
 * Interest Calculator Component
 * 
 * Displays interest estimate for an account and allows recalculation.
 * Shows details about the calculation method and allows creating
 * an operation for the estimated interest using account's default settings.
 * 
 * Features:
 * - Collapsible card (state persisted in localStorage)
 * - Responsive layout for mobile
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card } from './ui';
import { 
  getInterestEstimate, 
  calculateInterest, 
  InterestCalculationResult 
} from '../services/api';
import { formatCurrency, formatDate } from '../utils/formatters';
import type { BankAccount } from '@budgira/shared';

interface InterestCalculatorProps {
  account: BankAccount;
  onOperationCreated?: () => void;
}

// LocalStorage key for collapsed state
const COLLAPSED_KEY = 'interest-calculator-collapsed';

export function InterestCalculator({
  account,
  onOperationCreated,
}: InterestCalculatorProps) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [estimate, setEstimate] = useState<InterestCalculationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [includeSchedulers, setIncludeSchedulers] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem(COLLAPSED_KEY);
    // Default to collapsed (true) if no preference saved
    return saved === null ? true : saved === 'true';
  });

  // Only show for accounts with interest configured (method + frequency required)
  // Rate can come from history table now, not just the legacy interestRate field
  if (!account.interestCalculationMethod || !account.interestPaymentFrequency) {
    return null;
  }

  // Check if default settings are configured
  const hasDefaultSettings = !!(
    account.interestCategoryId &&
    account.interestCounterpartyId &&
    account.interestPaymentMethodId
  );

  const fetchEstimate = async (withSchedulers: boolean = includeSchedulers) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await getInterestEstimate(account.id, withSchedulers);
      setEstimate(result);
    } catch (err) {
      setError(t('accounts.interest.fetchError', 'Erreur lors du calcul des intérêts'));
      console.error('Error fetching interest estimate:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchEstimate(includeSchedulers);
  }, [account.id, includeSchedulers]);

  const handleRecalculate = () => {
    fetchEstimate(includeSchedulers);
  };

  const handleSchedulersToggle = (checked: boolean) => {
    setIncludeSchedulers(checked);
  };

  const handleToggleCollapse = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    localStorage.setItem(COLLAPSED_KEY, String(newState));
  };

  const handleCreateOperation = async () => {
    if (!hasDefaultSettings) {
      setError(t('accounts.interest.missingDefaults', 'Veuillez configurer les paramètres par défaut (catégorie, tiers, mode de paiement) dans les paramètres du compte.'));
      return;
    }

    setIsCreating(true);
    setError(null);
    try {
      const result = await calculateInterest(account.id, {
        createOperation: true,
        categoryId: account.interestCategoryId!,
        counterpartyId: account.interestCounterpartyId!,
        paymentMethodId: account.interestPaymentMethodId!,
      });

      if (result.operationCreated) {
        // Refresh estimate after creating operation
        await fetchEstimate();
        onOperationCreated?.();
      }
    } catch (err) {
      setError(t('accounts.interest.createError', 'Erreur lors de la création de l\'opération'));
      console.error('Error creating interest operation:', err);
    } finally {
      setIsCreating(false);
    }
  };

  const getMethodLabel = (method: string): string => {
    switch (method) {
      case 'daily':
        return t('accounts.form.calculationMethods.daily', 'Journalier');
      case 'biweekly_strict':
        return t('accounts.form.calculationMethods.biweekly_strict', 'Quinzaine stricte');
      case 'biweekly_simple':
        return t('accounts.form.calculationMethods.biweekly_simple', 'Quinzaine simplifiée');
      default:
        return method;
    }
  };

  return (
    <Card className="bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border-emerald-200 dark:border-emerald-800">
      {/* Header - always visible */}
      <div className="flex flex-col gap-3">
        {/* Title row with collapse button */}
        <div className="flex items-center justify-between">
          <button 
            onClick={handleToggleCollapse}
            className="flex items-center gap-1.5 sm:gap-2 hover:opacity-80 transition-opacity"
          >
            <svg 
              className={`w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-600 dark:text-emerald-400 transition-transform ${isCollapsed ? '-rotate-90' : ''}`} 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
            <svg className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-sm sm:text-lg font-medium text-emerald-900 dark:text-emerald-100">
              {t('accounts.interest.title', 'Estimation des intérêts')}
            </h3>
          </button>
          
          {/* Show amount in collapsed mode */}
          {isCollapsed && estimate && (
            <span className="text-sm sm:text-lg font-bold text-emerald-600 dark:text-emerald-400">
              {formatCurrency(estimate.totalInterest, account.currency)}
            </span>
          )}
        </div>

        {/* Action buttons - below title, responsive */}
        {!isCollapsed && (
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            {/* Forecast checkbox */}
            <label className="flex items-center gap-1.5 text-xs sm:text-sm text-slate-600 dark:text-slate-400 cursor-pointer">
              <input
                type="checkbox"
                checked={includeSchedulers}
                onChange={(e) => handleSchedulersToggle(e.target.checked)}
                className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-emerald-600 bg-white dark:bg-slate-700 border-slate-300 dark:border-slate-600 rounded focus:ring-emerald-500 focus:ring-2"
                disabled={isLoading}
              />
              <span className="flex items-center gap-1">
                <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {t('accounts.interest.forecast', 'Prévisionnel')}
              </span>
            </label>
            
            <Button
              variant="outline"
              size="sm"
              onClick={handleRecalculate}
              disabled={isLoading}
              className="border-emerald-300 dark:border-emerald-700 text-emerald-700 dark:text-emerald-300 text-xs sm:text-sm px-2 sm:px-3 py-1 sm:py-1.5"
            >
              {isLoading ? (
                <span className="flex items-center gap-1.5">
                  <span className="w-3.5 h-3.5 sm:w-4 sm:h-4 border-2 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
                  <span className="hidden sm:inline">{t('common.loading', 'Chargement...')}</span>
                </span>
              ) : (
                <>
                  <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  {t('accounts.interest.recalculate', 'Recalculer')}
                </>
              )}
            </Button>
          </div>
        )}
      </div>

      {/* Collapsible content */}
      {!isCollapsed && (
        <div className="mt-4">
          {error && (
            <div className="p-3 mb-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg text-red-600 dark:text-red-400 text-sm">
              {error}
            </div>
          )}

          {estimate ? (
            <div className="space-y-3 sm:space-y-4">
              {/* Main interest amount */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-3 p-3 sm:p-4 bg-white/50 dark:bg-slate-800/50 rounded-lg">
                <div>
                  <p className="text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                    {t('accounts.interest.estimatedAmount', 'Montant estimé')}
                    {estimate.includesSchedulers && (
                      <span className="ml-1.5 sm:ml-2 text-xs text-blue-600 dark:text-blue-400 font-medium">
                        ({t('accounts.interest.forecastMode', 'prévisionnel')})
                      </span>
                    )}
                  </p>
                  <p className="text-xl sm:text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                    {formatCurrency(estimate.totalInterest, account.currency)}
                  </p>
                </div>
                <div className="flex flex-col gap-1.5 sm:gap-2 sm:items-end">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleCreateOperation}
                    disabled={isCreating || !hasDefaultSettings}
                    className="bg-emerald-600 hover:bg-emerald-700 text-xs sm:text-sm"
                    title={!hasDefaultSettings ? t('accounts.interest.configureDefaultsFirst', 'Configurez d\'abord les paramètres par défaut dans le compte') : undefined}
                  >
                    {isCreating ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        {t('common.creating', 'Création...')}
                      </span>
                    ) : (
                      t('accounts.interest.createOperation', 'Créer opération')
                    )}
                  </Button>
                  {!hasDefaultSettings && (
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                      {t('accounts.interest.configureDefaults', 'Configurez les paramètres par défaut')}
                    </p>
                  )}
                </div>
              </div>

              {/* Details */}
              <div className="grid grid-cols-2 gap-2 sm:gap-4">
                <div>
                  <p className="text-[11px] sm:text-sm text-slate-500 dark:text-slate-400">{t('accounts.interest.period', 'Période')}</p>
                  <p className="text-xs sm:text-sm font-medium text-slate-900 dark:text-slate-100">
                    {formatDate(estimate.periodStart)} - {formatDate(estimate.periodEnd)}
                  </p>
                </div>
                <div>
                  <p className="text-[11px] sm:text-sm text-slate-500 dark:text-slate-400">{t('accounts.interest.paymentDate', 'Date de versement')}</p>
                  <p className="text-xs sm:text-sm font-medium text-slate-900 dark:text-slate-100">
                    {formatDate(estimate.paymentDate)}
                  </p>
                </div>
                <div className="col-span-2">
                  <p className="text-[11px] sm:text-sm text-slate-500 dark:text-slate-400 mb-1">
                    {t('accounts.interest.ratesApplied', 'Taux appliqués sur la période')}
                  </p>
                  {estimate.rateHistory && estimate.rateHistory.length > 0 ? (
                    <div className="space-y-1">
                      {estimate.rateHistory.map((rate, index) => (
                        <div key={index} className="flex items-center gap-2 text-xs sm:text-sm">
                          <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                            {rate.rate}%
                          </span>
                          <span className="text-slate-600 dark:text-slate-400">
                            {rate.endDate === null 
                              ? t('accounts.interest.rateSince', 'depuis le {{date}}', { date: formatDate(rate.startDate) })
                              : t('accounts.interest.rateFromTo', 'du {{start}} au {{end}}', { 
                                  start: formatDate(rate.startDate), 
                                  end: formatDate(rate.endDate) 
                                })
                            }
                          </span>
                          {rate.endDate === null && (
                            <span className="px-1.5 py-0.5 text-[10px] sm:text-xs bg-emerald-100 dark:bg-emerald-800 text-emerald-700 dark:text-emerald-300 rounded">
                              {t('accounts.interest.currentRate', 'en cours')}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs sm:text-sm font-medium text-slate-900 dark:text-slate-100">
                      {estimate.annualRate ?? account.interestRate ?? '-'}%
                    </p>
                  )}
                </div>
                <div className="col-span-2 sm:col-span-1">
                  <p className="text-[11px] sm:text-sm text-slate-500 dark:text-slate-400">{t('accounts.interest.method', 'Méthode')}</p>
                  <p className="text-xs sm:text-sm font-medium text-slate-900 dark:text-slate-100">
                    {getMethodLabel(estimate.method)}
                  </p>
                </div>
              </div>

              {/* Last calculation info */}
              {account.lastInterestCalculation && (
                <p className="text-xs text-slate-500 dark:text-slate-400">
                  {t('accounts.interest.lastCalculation', 'Dernier calcul')}: {formatDate(account.lastInterestCalculation)}
                </p>
              )}
            </div>
          ) : !isLoading && (
            <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
              {t('accounts.interest.noEstimate', 'Aucune estimation disponible. Configurez d\'abord les paramètres d\'intérêts du compte.')}
            </p>
          )}
        </div>
      )}
    </Card>
  );
}
